@let transfers = activeTransfers();

@if (isVisible()) {
  <div
    class="bg-muted border-line z-1 flex flex-col gap-3 rounded-xl border p-4"
    animate.enter="animate-slide-up"
    animate.leave="animate-slide-down"
    (animationend)="onLeave($event)"
    *transloco="let t"
  >
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <edu-icon [icon]="Icons.FILE_TRANSFER" />
        <h3 class="text-fg text-sm font-semibold">
          {{ t('components.compounds.fileTransferManager.header') }}
        </h3>

        @if (transfers.length > 0) {
          <span class="bg-muted text-fg-muted rounded-full px-2 py-0.5 text-xs font-medium">
            {{ transfers.length }}
          </span>
        }
      </div>

      <div class="flex items-center gap-1">
        <!-- We are not using the edu-button component because of the unique styles required that are only needed in this component -->
        <button
          (click)="isMinimized.set(!isMinimized())"
          type="button"
          class="text-fg-muted hover:text-fg hover:bg-hover flex h-6 w-6 cursor-pointer items-center justify-center rounded-md transition-colors"
          [attr.aria-label]="
            isMinimized()
              ? t('components.compounds.fileTransferManager.maximizeAriaLabel')
              : t('components.compounds.fileTransferManager.minimizeAriaLabel')
          "
        >
          <edu-icon [icon]="isMinimized() ? Icons.CHEVRON_UP : Icons.CHEVRON_DOWN" />
        </button>
        <button
          (click)="close(true)"
          type="button"
          class="text-fg-muted hover:text-fg hover:bg-hover flex h-6 w-6 cursor-pointer items-center justify-center rounded-md transition-colors"
          [attr.aria-label]="t('components.compounds.fileTransferManager.closeAriaLabel')"
        >
          <edu-icon [icon]="Icons.X_MARK" />
        </button>
      </div>
    </div>

    @if (!isMinimized()) {
      <!-- Divider -->
      <div class="border-line border-t"></div>

      <!-- Empty state -->
      @if (transfers.length === 0) {
        <div class="flex flex-col items-center gap-2 py-6">
          <edu-icon [icon]="Icons.FILE_TRANSFER_EMPTY" />
          <p class="text-fg-muted text-xs">
            {{ t('components.compounds.fileTransferManager.empty') }}
          </p>
        </div>
      } @else {
        <ul class="flex flex-col gap-2">
          @for (transfer of transfers; track transfer.id) {
            <li class="bg-muted border-line-strong relative overflow-hidden rounded-lg border p-2">
              <div class="flex items-center gap-3">
                <!-- State icon -->
                <div
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                  [ngClass]="{
                    'bg-danger text-danger-contrast': transfer.state === TransferState.FAILED,
                    'bg-info text-info-contrast': transfer.state === TransferState.PROGRESS,
                    'bg-muted text-fg-muted': transfer.state === TransferState.DONE,
                  }"
                >
                  @switch (transfer.state) {
                    @case (TransferState.PREPARING) {
                      <edu-icon [icon]="Icons.FILE_TRANSFER_STATE_PREPARING" />
                    }
                    @case (TransferState.PENDING) {
                      <edu-icon [icon]="Icons.FILE_TRANSFER_STATE_PREPARING" />
                    }
                    @case (TransferState.PROGRESS) {
                      <edu-icon
                        [icon]="Icons.FILE_TRANSFER_STATE_PROGRESS"
                        iconClasses="animate-spin"
                      />
                    }
                    @case (TransferState.DONE) {
                      <edu-icon [icon]="Icons.FILE_TRANSFER_STATE_DONE" />
                    }
                    @case (TransferState.FAILED) {
                      <edu-icon [icon]="Icons.FILE_TRANSFER_STATE_FAILED" />
                    }
                  }
                </div>

                <!-- File name + error -->
                <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span
                    class="truncate text-sm leading-tight font-medium"
                    [ngClass]="{
                      'text-fg-muted': transfer.state === TransferState.DONE,
                      'text-fg': transfer.state !== TransferState.DONE,
                    }"
                    [title]="transfer.fileName"
                  >
                    {{ transfer.fileName }}
                  </span>
                  @if (transfer.errorMessage) {
                    <span class="text-danger truncate text-xs">{{ transfer.errorMessage }}</span>
                  }
                </div>

                <!-- Status badge / progress % -->
                <span
                  class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums"
                  [ngClass]="{
                    'bg-muted text-fg-muted': transfer.state === TransferState.DONE,
                    'bg-danger text-danger-contrast': transfer.state === TransferState.FAILED,
                    'bg-info text-info-contrast': transfer.state === TransferState.PROGRESS,
                  }"
                >
                  @switch (transfer.state) {
                    @case (TransferState.PREPARING) {
                      {{ t('components.compounds.fileTransferManager.states.preparing') }}
                    }
                    @case (TransferState.PENDING) {
                      {{ t('components.compounds.fileTransferManager.states.pending') }}
                    }
                    @case (TransferState.PROGRESS) {
                      {{ transfer.progress || 0 }}%
                    }
                    @case (TransferState.DONE) {
                      {{ t('components.compounds.fileTransferManager.states.done') }}
                    }
                    @case (TransferState.FAILED) {
                      {{ t('components.compounds.fileTransferManager.states.failed') }}
                    }
                  }
                </span>

                <!-- Direction icon -->
                <edu-icon
                  [attr.aria-label]="
                    transfer.direction === 'upload'
                      ? t('components.compounds.fileTransferManager.uploadAriaLabel')
                      : t('components.compounds.fileTransferManager.downloadAriaLabel')
                  "
                  [icon]="transfer.direction === 'upload' ? Icons.UPLOAD : Icons.DOWNLOAD"
                />
              </div>

              <!-- Progress bar as bottom border — no layout shift -->
              @if (
                transfer.state !== TransferState.DONE && transfer.state !== TransferState.FAILED
              ) {
                <div
                  role="progressbar"
                  [attr.aria-valuenow]="transfer.progress ?? 0"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  [attr.aria-label]="transfer.fileName"
                  class="bg-info absolute bottom-0 left-0 h-0.5 transition-[width] duration-300"
                  [class.animate-pulse]="
                    transfer.state === TransferState.PREPARING || transfer.progress === undefined
                  "
                  [style.width.%]="
                    transfer.state === TransferState.PREPARING || transfer.progress === undefined
                      ? 100
                      : transfer.progress
                  "
                ></div>
              }
            </li>
          }
        </ul>
      }
    }
  </div>
}
